import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, apiErr } from "@/context/AuthContext";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", password: "", password2: "", terms: false });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password2) return toast.error("Şifreler eşleşmiyor.");
    if (form.password.length < 6) return toast.error("Şifre en az 6 karakter olmalı.");
    if (!form.terms) return toast.error("Kullanım şartlarını kabul etmelisiniz.");
    setLoading(true);
    try {
      await register({ name: form.name, company: form.company, email: form.email, phone: form.phone, password: form.password, accept_terms: true });
      toast.success("Hesabınız oluşturuldu.");
      nav("/dashboard");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-primary p-12 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="relative"><Logo variant="white" /></div>
        <div className="relative">
          <h2 className="text-3xl font-extrabold tracking-tighter text-white leading-tight">Dakikalar içinde<br />kripto ödemelere başlayın.</h2>
          <p className="mt-4 text-white/70 max-w-sm leading-relaxed">Ücretsiz hesap oluşturun, API anahtarlarınızı alın ve ilk ödemenizi kabul edin.</p>
        </div>
        <div className="relative text-xs text-white/50">© {new Date().getFullYear()} Coinbuy.org</div>
      </div>

      <div className="flex flex-col p-6 sm:p-12 overflow-y-auto">
        <div className="flex items-center justify-between">
          <Link to="/" className="lg:hidden"><Logo /></Link>
          <Link to="/" className="hidden lg:inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft size={15} /> Ana Sayfa
          </Link>
          <ThemeToggle />
        </div>

        <div className="flex-1 flex items-center justify-center py-8">
          <div className="w-full max-w-sm">
            <h1 className="text-2xl font-extrabold tracking-tighter">Hesap Oluştur</h1>
            <p className="mt-2 text-sm text-muted-foreground">İşletmeniz için Coinbuy hesabı açın.</p>

            <form onSubmit={submit} className="mt-6 space-y-3.5">
              <div>
                <Label className="text-sm">Ad Soyad</Label>
                <Input required value={form.name} onChange={set("name")} data-testid="register-name" className="mt-1.5" placeholder="Adınız Soyadınız" />
              </div>
              <div>
                <Label className="text-sm">Firma / Mağaza Adı</Label>
                <Input required value={form.company} onChange={set("company")} data-testid="register-company" className="mt-1.5" placeholder="Mağazanız" />
              </div>
              <div>
                <Label className="text-sm">E-posta</Label>
                <Input required type="email" value={form.email} onChange={set("email")} data-testid="register-email" className="mt-1.5" placeholder="ornek@eposta.com" />
              </div>
              <div>
                <Label className="text-sm">Telefon</Label>
                <Input value={form.phone} onChange={set("phone")} data-testid="register-phone" className="mt-1.5" placeholder="+90 5xx xxx xx xx" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Şifre</Label>
                  <Input required type="password" value={form.password} onChange={set("password")} data-testid="register-password" className="mt-1.5" placeholder="••••••••" />
                </div>
                <div>
                  <Label className="text-sm">Şifre Tekrarı</Label>
                  <Input required type="password" value={form.password2} onChange={set("password2")} data-testid="register-password2" className="mt-1.5" placeholder="••••••••" />
                </div>
              </div>
              <label className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer pt-1">
                <Checkbox checked={form.terms} onCheckedChange={(v) => setForm((f) => ({ ...f, terms: !!v }))} data-testid="register-terms" className="mt-0.5" />
                <span><Link to="/terms" className="text-primary hover:underline">Kullanım şartlarını</Link> ve <Link to="/privacy" className="text-primary hover:underline">gizlilik politikasını</Link> kabul ediyorum.</span>
              </label>
              <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full rounded-full h-11">
                {loading ? <Loader2 className="animate-spin" size={17} /> : "Hesap Oluştur"}
              </Button>
            </form>

            <p className="mt-5 text-center text-sm text-muted-foreground">
              Zaten hesabınız var mı?{" "}
              <Link to="/login" className="font-semibold text-primary hover:underline">Giriş Yapın</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
